import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import { Card, Grow, Skeleton, useTheme } from "@mui/material";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";

import useCountUp from "../../hooks/use-count-up";
import { pulseSx, staggerDelay, surfaceCardSx } from "../styles";

type Props = {
  title: string;
  value: number;
  index?: number;
  isLoading?: boolean;
  isRefreshing?: boolean;
};

const SlaCard = ({
  title,
  value,
  index = 0,
  isLoading = false,
  isRefreshing = false,
}: Props) => {
  const theme = useTheme();
  const percent = Math.max(0, Math.min(100, value));
  const animated = useCountUp(percent);
  const radius = 46;
  const circumference = Math.PI * radius;
  const dashOffset = circumference - (animated / 100) * circumference;
  const gaugeColor =
    percent >= 80
      ? theme.palette.success.main
      : percent >= 60
        ? theme.palette.warning.main
        : theme.palette.error.main;

  return (
    <Card sx={{ ...surfaceCardSx("success"), height: "100%" }}>
      <MDBox
        p={2.25}
        height="100%"
        minHeight="5.75rem"
        display="flex"
        alignItems="center"
        sx={{ "& > *": { width: "100%" } }}
      >
        {isLoading ? (
          <MDBox display="flex" alignItems="center" gap={1.25}>
            <Skeleton variant="rounded" width={32} height={32} />
            <MDBox flex={1}>
              <Skeleton variant="text" width="80%" height={14} />
              <Skeleton variant="text" width="45%" height={26} />
            </MDBox>
            <Skeleton variant="rounded" width={54} height={30} />
          </MDBox>
        ) : (
          <Grow in timeout={520} style={{ transitionDelay: staggerDelay(index) }}>
            <MDBox
              display="flex"
              alignItems="center"
              gap={1.25}
              sx={pulseSx(isRefreshing)}
              height="100%"
            >
              <MDBox
                variant="gradient"
                bgColor="success"
                color="white"
                width="2rem"
                height="2rem"
                borderRadius="lg"
                display="flex"
                justifyContent="center"
                alignItems="center"
                shadow="sm"
                sx={{ flexShrink: 0 }}
              >
                <TrackChangesIcon fontSize="small" />
              </MDBox>
              <MDBox lineHeight={1.15} sx={{ minWidth: 0, flex: 1 }}>
                <MDTypography
                  variant="caption"
                  fontWeight="medium"
                  color="text"
                  sx={{ fontSize: "0.7rem", display: "block" }}
                >
                  {title}
                </MDTypography>
                <MDTypography variant="h5" fontWeight="bold" mt={0.1}>
                  {new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(animated)}%
                </MDTypography>
              </MDBox>
              <MDBox display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
                <svg width="58" height="34" viewBox="0 0 124 74" aria-hidden>
                  <defs>
                    <linearGradient id="slaGaugeGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={theme.palette.primary.main} />
                      <stop offset="100%" stopColor={gaugeColor} />
                    </linearGradient>
                  </defs>
                  <path
                    d="M 16 68 A 46 46 0 0 1 108 68"
                    fill="none"
                    stroke={theme.palette.grey[200]}
                    strokeWidth="12"
                    strokeLinecap="round"
                  />
                  <path
                    d="M 16 68 A 46 46 0 0 1 108 68"
                    fill="none"
                    stroke="url(#slaGaugeGradient)"
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                  />
                </svg>
              </MDBox>
            </MDBox>
          </Grow>
        )}
      </MDBox>
    </Card>
  );
};


export default SlaCard;
